/**
 * A/B Testing Framework for Conversion Optimization
 * Enables testing of trial lengths, CTA variants, and other conversion elements
 */

// Import consent manager for GDPR compliance
import { consentManager } from '../services/consentManager';

interface ABTest {
  id: string;
  name: string;
  variants: ABVariant[];
  trafficAllocation: number[]; // Percentage allocation for each variant
  targetMetric: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  startDate?: Date;
  endDate?: Date;
}

interface ABVariant {
  id: string;
  name: string;
  config: any;
  weight: number; // Traffic percentage (0-100)
}

interface ABTestResult {
  testId: string;
  variantId: string;
  userId?: string;
  sessionId: string;
  event: string;
  timestamp: number;
  metadata?: any;
}

// Active A/B tests configuration
const ACTIVE_TESTS: ABTest[] = [
  {
    id: 'trial_length_optimization',
    name: 'Trial Length Optimization',
    variants: [
      { id: 'control_14_days', name: '14 Days (Control)', config: { trialDays: 14 }, weight: 34 },
      { id: 'variant_7_days', name: '7 Days', config: { trialDays: 7 }, weight: 33 },
      { id: 'variant_21_days', name: '21 Days', config: { trialDays: 21 }, weight: 33 }
    ],
    trafficAllocation: [34, 33, 33],
    targetMetric: 'trial_to_paid_conversion',
    status: 'running'
  },
  {
    id: 'cta_optimization',
    name: 'CTA Text Optimization',
    variants: [
      { id: 'control_start_trial', name: 'Start Free Trial (Control)', config: { ctaText: 'Start Free Trial' }, weight: 25 },
      { id: 'variant_get_started', name: 'Get Started Free', config: { ctaText: 'Get Started Free' }, weight: 25 },
      { id: 'variant_try_now', name: 'Try Bot Blender Now', config: { ctaText: 'Try Bot Blender Now' }, weight: 25 },
      { id: 'variant_join_traders', name: 'Join 1000+ Traders', config: { ctaText: 'Join 1000+ Traders' }, weight: 25 }
    ],
    trafficAllocation: [25, 25, 25, 25],
    targetMetric: 'cta_click_rate',
    status: 'running'
  },
  {
    id: 'pricing_display_test',
    name: 'Pricing Display Test',
    variants: [
      { id: 'control_monthly', name: 'Monthly Pricing (Control)', config: { defaultView: 'monthly' }, weight: 50 },
      { id: 'variant_annual', name: 'Annual Pricing Default', config: { defaultView: 'annual' }, weight: 50 }
    ],
    trafficAllocation: [50, 50],
    targetMetric: 'upgrade_conversion',
    status: 'running'
  }
];

// Helper function to get or create session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('ab_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('ab_session_id', sessionId);
  }
  return sessionId;
};

// Helper function to hash string for consistent variant assignment
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * A/B Testing utilities
 */
export const ABTestTracker = {
  // Get assigned variant for a test
  getVariant: (testId: string, userId?: string): ABVariant | null => {
    const test = ACTIVE_TESTS.find(t => t.id === testId && t.status === 'running');
    if (!test) return null;

    // Check if user has personalization consent
    const hasPersonalizationConsent = consentManager.hasConsentFor('personalization');
    
    if (!hasPersonalizationConsent) {
      console.log(`[ABTestTracker] Personalization consent not granted for test ${testId}, using control variant`);
      // Return first variant (control) as default when no consent
      return test.variants[0];
    }

    // Use consistent identifier for assignment
    const identifier = userId || getSessionId();
    const storageKey = `ab_variant_${testId}`;
    
    // Check if already assigned
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const variant = test.variants.find(v => v.id === stored);
      if (variant) return variant;
    }

    // Assign new variant based on hash
    const hash = hashString(`${testId}_${identifier}`);
    const bucket = hash % 100;
    
    let cumulativeWeight = 0;
    for (const variant of test.variants) {
      cumulativeWeight += variant.weight;
      if (bucket < cumulativeWeight) {
        // Store assignment
        localStorage.setItem(storageKey, variant.id);
        
        // Track assignment
        ABTestTracker.trackTestAssignment(testId, variant.id, userId);
        
        return variant;
      }
    }
    
    // Fallback to first variant
    const fallbackVariant = test.variants[0];
    localStorage.setItem(storageKey, fallbackVariant.id);
    return fallbackVariant;
  },

  // Track test assignment
  trackTestAssignment: (testId: string, variantId: string, userId?: string) => {
    const hasPersonalizationConsent = consentManager.hasConsentFor('personalization');
    
    if (!hasPersonalizationConsent) {
      console.log(`[ABTestTracker] Personalization consent not granted, skipping assignment tracking for ${testId}`);
      return;
    }

    const result: ABTestResult = {
      testId,
      variantId,
      userId,
      sessionId: getSessionId(),
      event: 'test_assigned',
      timestamp: Date.now()
    };

    // Store locally for analysis
    const results = JSON.parse(localStorage.getItem('ab_test_results') || '[]');
    results.push(result);
    
    // Keep only last 100 results
    if (results.length > 100) {
      results.shift();
    }
    
    localStorage.setItem('ab_test_results', JSON.stringify(results));

    // Track with Omnisend
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'ab_test_assigned', {
        test_id: testId,
        variant_id: variantId,
        user_id: userId,
        session_id: getSessionId()
      }]);
    }
  },

  // Track conversion event for A/B test
  trackConversion: (testId: string, event: string, metadata?: any, userId?: string) => {
    const hasPersonalizationConsent = consentManager.hasConsentFor('personalization');
    
    if (!hasPersonalizationConsent) {
      console.log(`[ABTestTracker] Personalization consent not granted, skipping conversion tracking for ${testId}`);
      return;
    }

    const variant = ABTestTracker.getVariant(testId, userId);
    if (!variant) return;

    const result: ABTestResult = {
      testId,
      variantId: variant.id,
      userId,
      sessionId: getSessionId(),
      event,
      timestamp: Date.now(),
      metadata
    };

    // Store locally
    const results = JSON.parse(localStorage.getItem('ab_test_results') || '[]');
    results.push(result);
    localStorage.setItem('ab_test_results', JSON.stringify(results));

    // Track with Omnisend
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'ab_test_conversion', {
        test_id: testId,
        variant_id: variant.id,
        event,
        metadata,
        user_id: userId,
        session_id: getSessionId()
      }]);
    }
  },

  // Specific test tracking methods
  trackTrialLengthTest: (variant: '7_day' | '14_day' | '21_day', userId?: string) => {
    ABTestTracker.trackConversion('trial_length_optimization', 'trial_started', {
      trial_length: variant,
      signup_method: 'ab_test'
    }, userId);
  },

  trackCTATest: (variant: string, position: string, clicked: boolean = true, userId?: string) => {
    const event = clicked ? 'cta_clicked' : 'cta_viewed';
    ABTestTracker.trackConversion('cta_optimization', event, {
      cta_variant: variant,
      position,
      conversion_goal: 'trial_signup'
    }, userId);
  },

  trackPricingDisplayTest: (variant: 'monthly' | 'annual', action: string, userId?: string) => {
    ABTestTracker.trackConversion('pricing_display_test', action, {
      pricing_variant: variant,
      action_type: action // 'view', 'select_plan', 'upgrade'
    }, userId);
  },

  // Get test configuration for UI components
  getTestConfig: (testId: string, userId?: string): any => {
    const variant = ABTestTracker.getVariant(testId, userId);
    return variant?.config || null;
  },

  // Check if user is in specific test variant
  isInVariant: (testId: string, variantId: string, userId?: string): boolean => {
    const variant = ABTestTracker.getVariant(testId, userId);
    return variant?.id === variantId;
  },

  // Get all active test variants for current user
  getAllActiveVariants: (userId?: string): Record<string, ABVariant> => {
    const variants: Record<string, ABVariant> = {};
    
    ACTIVE_TESTS.forEach(test => {
      if (test.status === 'running') {
        const variant = ABTestTracker.getVariant(test.id, userId);
        if (variant) {
          variants[test.id] = variant;
        }
      }
    });
    
    return variants;
  },

  // Get test results for analysis (local data only)
  getTestResults: (testId?: string): ABTestResult[] => {
    const allResults = JSON.parse(localStorage.getItem('ab_test_results') || '[]');
    return testId ? allResults.filter((r: ABTestResult) => r.testId === testId) : allResults;
  },

  // Force assign user to specific variant (for testing)
  forceVariant: (testId: string, variantId: string) => {
    const test = ACTIVE_TESTS.find(t => t.id === testId);
    const variant = test?.variants.find(v => v.id === variantId);
    
    if (variant) {
      localStorage.setItem(`ab_variant_${testId}`, variantId);
      ABTestTracker.trackTestAssignment(testId, variantId);
      return variant;
    }
    
    return null;
  },

  // Clear all test assignments (for testing)
  clearAllAssignments: () => {
    ACTIVE_TESTS.forEach(test => {
      localStorage.removeItem(`ab_variant_${test.id}`);
    });
    localStorage.removeItem('ab_test_results');
    sessionStorage.removeItem('ab_session_id');
  }
};

export default ABTestTracker;